"use client";

import React from "react";
import { cn } from "@/lib/utils";
import { Info, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";

type InfoBoxVariant = "info" | "warning" | "success" | "error";

interface InfoBoxProps {
  children: React.ReactNode;
  variant?: InfoBoxVariant;
  icon?: React.ReactNode;
  className?: string;
  title?: string;
}

const variantStyles: Record<
  InfoBoxVariant,
  {
    container: string;
    icon: string;
    title: string;
    text: string;
  }
> = {
  info: {
    container:
      "border-gothia-gold/40 bg-gothia-gold/10 backdrop-blur-sm",
    icon: "text-gothia-gold",
    title: "text-gothia-gold",
    text: "text-gray-200",
  },
  warning: {
    container:
      "border-amber-500/30 bg-amber-950/20 backdrop-blur-sm",
    icon: "text-amber-400",
    title: "text-amber-200",
    text: "text-amber-100",
  },
  success: {
    container:
      "border-emerald-500/30 bg-emerald-950/20 backdrop-blur-sm",
    icon: "text-emerald-400",
    title: "text-emerald-200",
    text: "text-emerald-100",
  },
  error: {
    container:
      "border-red-500/30 bg-red-950/20 backdrop-blur-sm",
    icon: "text-red-400",
    title: "text-red-200",
    text: "text-red-100",
  },
};

const defaultIcons: Record<InfoBoxVariant, React.ReactNode> = {
  info: <Info className="h-5 w-5" />,
  warning: <AlertTriangle className="h-5 w-5" />,
  success: <CheckCircle className="h-5 w-5" />,
  error: <AlertCircle className="h-5 w-5" />,
};

export function InfoBox({
  children,
  variant = "info",
  icon,
  className,
  title,
}: InfoBoxProps) {
  const styles = variantStyles[variant];
  const displayIcon = icon ?? defaultIcons[variant];

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border p-4 backdrop-blur-sm",
        styles.container,
        className
      )}>
      <div className={cn("mt-0.5 shrink-0", styles.icon)}>{displayIcon}</div>
      <div className="flex-1">
        {title && (
          <div className={cn("mb-1.5 font-semibold", styles.title)}>
            {title}
          </div>
        )}
        <div className={cn("text-body-sm", styles.text)}>{children}</div>
      </div>
    </div>
  );
}

